/**
 * As conferências registradas (E172.F7.T03), a vigente primeiro: o estado, o instante da consulta
 * com o fuso, o endereço, os campos confrontados ou o motivo, e o print. A indisponibilidade é
 * dita como tal, nunca como divergência. A consulta automática do sistema (E174.F1, ADR-0125) diz
 * que foi automática e mostra o SHA-256 da resposta guardada no lugar do print; os hashes
 * confrontados saem abreviados, com o valor inteiro no título.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Badge, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  hashAbreviado,
  instanteDoExame,
  lerPrint,
  type ConferenciaRegistradaData,
} from '@/lib/conferenciaNoPortal';

const t = ptBR.conferenciaNoPortal;
const tr = t.registradas;
const HASH = /^[0-9a-f]{64}$/;

function Valor({ v }: { v: string | null }): JSX.Element {
  if (v === null) return <>—</>;
  return HASH.test(v) ? <MonoData title={v}>{hashAbreviado(v)}</MonoData> : <>{v}</>;
}

function Campos({ r }: { r: ConferenciaRegistradaData }): JSX.Element | null {
  const { campos } = r.conferencia;
  if (campos.length === 0) return null;
  return (
    <table className="w-full text-body-sm" data-testid="portal-campos">
      <caption className="text-left text-caption text-fg-dim">{tr.campos}</caption>
      <thead>
        <tr className="text-left text-caption text-fg-dim">
          <th scope="col">{t.formulario.campo}</th>
          <th scope="col">{tr.noPortal}</th>
          <th scope="col">{tr.noMaterial}</th>
          <th scope="col">{t.formulario.resultado}</th>
        </tr>
      </thead>
      <tbody>
        {campos.map((c, i) => (
          <tr key={`${c.rotulo}-${i}`} data-resultado={c.resultado}>
            <td>{c.rotulo}</td>
            <td>
              <Valor v={c.no_portal} />
            </td>
            <td>
              <Valor v={c.no_material} />
            </td>
            <td>{t.resultado[c.resultado]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface PrintProps {
  r: ConferenciaRegistradaData;
  client: AxiosInstance;
  caseId: string;
}

function RespostaGuardada({ r }: { r: ConferenciaRegistradaData }): JSX.Element | null {
  const sha = r.conferencia.consulta_automatica?.resposta_sha256 ?? null;
  if (sha === null) return null;
  return (
    <p className="text-caption text-fg-muted" data-testid="portal-resposta" title={sha}>
      {tr.respostaGuardada} · SHA-256 <MonoData>{hashAbreviado(sha)}</MonoData>
    </p>
  );
}

function Print({ r, client, caseId }: PrintProps): JSX.Element | null {
  const [abrindo, setAbrindo] = useState(false);
  const print = r.conferencia.print_da_pagina;
  if (print === null && r.conferencia.origem === 'sistema') return null;
  if (print === null) return <p className="text-caption text-fg-dim">{tr.semPrint}</p>;
  async function abrir(): Promise<void> {
    setAbrindo(true);
    try {
      const blob = await lerPrint(client, caseId, r.id);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } finally {
      setAbrindo(false);
    }
  }
  return (
    <p className="text-caption text-fg-muted" data-testid="portal-print" title={print.sha256}>
      <Button type="button" variant="secondary" size="sm" disabled={abrindo} onClick={abrir}>
        {tr.verPrint}
      </Button>{' '}
      SHA-256 <MonoData>{hashAbreviado(print.sha256)}</MonoData>
    </p>
  );
}

function Registrada({
  r,
  vigente,
  client,
  caseId,
}: PrintProps & { vigente: boolean }): JSX.Element {
  const c = r.conferencia;
  return (
    <li className="space-y-sc-1 border-t border-border pt-sc-2" data-testid="portal-registrada">
      <p className="text-body-sm text-fg" data-estado={c.estado}>
        <strong>{t.estado[c.estado]}</strong>
        {vigente ? (
          <Badge className="ml-sc-2" data-testid="portal-vigente">
            {tr.vigente}
          </Badge>
        ) : null}
      </p>
      {c.origem === 'sistema' ? (
        <p className="text-caption text-fg-muted" data-testid="portal-automatica">
          {tr.automatica}
        </p>
      ) : null}
      {c.consultado_em ? (
        <p className="text-caption text-fg-muted" data-testid="portal-consultado-em">
          {tr.consultadoEm} {instanteDoExame(c.consultado_em, { segundos: true })}
        </p>
      ) : null}
      {c.url ? (
        <p className="break-all text-caption text-fg-muted">
          {c.url}
          {c.endereco_do_material ? null : ` (${tr.enderecoInformado})`}
        </p>
      ) : null}
      <Campos r={r} />
      {c.motivo ? (
        <p className="text-body-sm" data-testid="portal-motivo">
          {tr.motivo}: {c.motivo}
        </p>
      ) : null}
      <RespostaGuardada r={r} />
      <Print r={r} client={client} caseId={caseId} />
      <p className="text-caption text-fg-dim">
        {tr.registradaEm} {instanteDoExame(r.registrada_em)}
      </p>
    </li>
  );
}

export interface ConferenciasRegistradasProps {
  registradas: ConferenciaRegistradaData[];
  client: AxiosInstance;
  caseId: string;
}

export function ConferenciasRegistradas({
  registradas,
  client,
  caseId,
}: ConferenciasRegistradasProps): JSX.Element {
  return (
    <section className="space-y-sc-2" aria-labelledby="portal-registradas-titulo">
      <h4 id="portal-registradas-titulo" className="text-body text-fg">
        {tr.titulo}
      </h4>
      {registradas.length === 0 ? (
        <p className="text-body-sm text-fg-muted" data-testid="portal-nenhuma">
          {tr.nenhuma}
        </p>
      ) : (
        <ul className="space-y-sc-2">
          {registradas.map((r, i) => (
            <Registrada key={r.id} r={r} vigente={i === 0} client={client} caseId={caseId} />
          ))}
        </ul>
      )}
    </section>
  );
}
